import React, { useCallback, useState } from 'react';
import { NSButtonBase } from 'bricks/NSButton/NSButton';
import './ProFormaQuickStart.scss';
import { NSNavTabs } from 'bricks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { ProFormaModelIds } from 'constants/proForma';
import { SignUpStage, useSignUpContext } from '../SignUp/SignUpContext';
import {
    BASIC_INFORMATION, DEVELOPMENT, initialProFormaValues, OPERATIONS, PRO_FORMA_QUICK_START_TAB,
} from './constants';
import BasicInformation from './BasicInformation/BasicInformation';
import Development from './Development/Development';
import Operations from './Operations/Operations';
import { AssetTypeToProFormaModelIdMap, ProFormaQuickStartValues } from './types';

const ProFormaQuickStart: React.FC = () => {
    const {
        setCurrentStage, updateStageData, stageData, currentStageData,
    } = useSignUpContext();
    const storedStageData = currentStageData as ProFormaQuickStartValues;

    const [proFormaValues, setProFormaValues] = useState(storedStageData || initialProFormaValues);
    const [activeTabName, setActiveTabName] = useState(BASIC_INFORMATION);

    const handleCreate = () => {
        updateStageData(proFormaValues);
        setCurrentStage(SignUpStage.PRO_FORMA_DASHBOARD);
    };

    const handleBackButton = () => {
        updateStageData(proFormaValues);
        setCurrentStage(SignUpStage.BOE_CALCULATOR);
    };

    const updateValues = (stepKey: string, values: any) => {
        setProFormaValues(prev => ({ ...prev, [stepKey]: values }));
    };

    const updateBasicInformation = useCallback(
        (values: any) => updateValues('basicInformation', values),
        [],
    );

    const updateDevelopment = useCallback(
        (values: any) => updateValues('development', values),
        [],
    );
    const updateOperations = useCallback(
        (values: any) => updateValues('operations', values),
        [],
    );

    const boeData = stageData[SignUpStage.BOE_CALCULATOR];
    const proFormaModelId = AssetTypeToProFormaModelIdMap[boeData?.propertyType.value] || ProFormaModelIds.APARTMENT;

    return (
        <div className="ProFormaQuickStart d-flex">
            <div className="ProFormaQuickStart__left-panel p-3 pb-0">
                <NSNavTabs
                    containerClassName="d-flex mt-0"
                    navClassName="w-100 d-flex"
                    navItems={PRO_FORMA_QUICK_START_TAB.map((navName, index) => ({
                        callback: () => setActiveTabName(navName),
                        name: navName,
                        isActive: activeTabName === navName,
                        id: `pro-forma-quick-start-${index}`,
                        className: 'text-dark text-nowrap px-4',
                    }))}
                />
                {activeTabName === BASIC_INFORMATION && (
                    <BasicInformation
                        values={proFormaValues.basicInformation}
                        onUpdate={updateBasicInformation}
                        proFormaModelId={proFormaModelId}
                    />
                )}
                {activeTabName === DEVELOPMENT && (
                    <Development
                        values={proFormaValues.development}
                        dealUnits={proFormaValues.basicInformation.buildings}
                        dealSF={proFormaValues.basicInformation.builtGrossSquareFootage}
                        proFormaModelId={proFormaModelId}
                        onUpdate={updateDevelopment}
                    />
                )}
                {activeTabName === OPERATIONS && (
                    <Operations
                        values={proFormaValues.operations}
                        dealUnits={proFormaValues.basicInformation.buildings}
                        dealSF={proFormaValues.basicInformation.builtGrossSquareFootage}
                        proFormaModelId={proFormaModelId}
                        onUpdate={updateOperations}
                    />
                )}
            </div>
            <div
                className="ProFormaQuickStart__right-panel d-flex flex-column justify-content-center p-3 pb-0"
            >
                <div
                    onClick={() => handleBackButton()}
                    className="cursor--pointer text-primary d-flex align-items-center"
                    role="presentation"
                >
                    <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                    <h4 className="pl-1 font-weight-lighter">Back</h4>
                </div>
                <div className="ProFormaQuickStart__right-panel__content">
                    <h1 className="text-white font-weight-bold">
                        {`Now, let's quickly make a pro forma for this ${boeData?.propertyType.label.toLowerCase()}`}
                    </h1>
                    <p className="text-white">
                        Modify the pre-filled data to suit the details of your deal.
                    </p>
                    <NSButtonBase
                        className="btn btn-primary w-100"
                        callback={handleCreate}
                    >
                        Create
                    </NSButtonBase>
                </div>
            </div>
        </div>
    );
};

export default ProFormaQuickStart;
